import * as Sentry from '@sentry/react';
import { type ApiSuccessBody, parseErrorResponse, SessionExpiredError } from './errors';

interface ApiFetchOptions extends RequestInit {
    /** Si true, omite el dedupe + retry de 401. Usado por el propio /auth/refresh. */
    skipRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

// Sin timeout, un fetch cuyo socket queda colgado (request enviado, respuesta que nunca vuelve
// — p.ej. el proxy de dev o el borde no entregan el 200) deja la promesa PENDING para siempre:
// el spinner del caller gira infinito y nunca cae a onError. Un techo duro garantiza que toda
// request termina (resuelve o rechaza). Ver caso enroll-totp colgado en "Generando código…".
const REQUEST_TIMEOUT_MS = 20_000;

/** Error de timeout legible (lo consume el errorMapping del front como cualquier Error). */
export class RequestTimeoutError extends Error {
    constructor() {
        super('La solicitud tardó demasiado. Revisa tu conexión e intenta de nuevo.');
        this.name = 'RequestTimeoutError';
    }
}

/** `fetch` con techo de tiempo: ante abort por timeout re-tira `RequestTimeoutError` (no AbortError crudo). */
async function fetchWithTimeout(input: string, init: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => {
        controller.abort();
    }, REQUEST_TIMEOUT_MS);
    try {
        return await fetch(input, { ...init, signal: controller.signal });
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            throw new RequestTimeoutError();
        }
        throw error;
    } finally {
        clearTimeout(timer);
    }
}

// El rbac expone 2 APIs públicas bajo base paths distintos del custom domain compartido:
// PublicApi sin auth (/auth/* + /healthcheck) y BackofficeApi con cookie/W4. En local ambas
// bases apuntan a /api y el proxy de Vite (vite.config.ts) hace el split por ruta; en deploy
// apuntan al custom domain con su base path.
//   VITE_API_AUTH_BASE_URL → PublicApi    (p.ej. https://api-dev.tufiado.com/rbac)
//   VITE_API_BASE_URL      → BackofficeApi (p.ej. https://api-dev.tufiado.com/rbac-backoffice)
const BACKOFFICE_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const AUTH_BASE_URL = import.meta.env.VITE_API_AUTH_BASE_URL || BACKOFFICE_BASE_URL;

/**
 * Base URL según la ruta: rutas públicas (/auth/*, /healthcheck, /self-register/*, /public/*) →
 * Public API; el resto → Backoffice API. El autoregistro y la password-policy van por la Public API
 * (sin cookie/W4, authType NONE), igual que login.
 */
function baseUrlForPath(path: string): string {
    return path.startsWith('/auth') ||
        path.startsWith('/healthcheck') ||
        path.startsWith('/self-register') ||
        path.startsWith('/public')
        ? AUTH_BASE_URL
        : BACKOFFICE_BASE_URL;
}

async function tryRefresh(): Promise<boolean> {
    refreshPromise ??= (async () => {
        try {
            // DEC-064: /auth/refresh es cookie-only. El backend lee el refreshToken de la cookie
            // `refresh` y el tenantId de la cookie `pool`; el front NO manda body. Así una pestaña
            // recién abierta (sin haber cargado /me/profile aún) puede refrescar y no rebota a login.
            const r = await fetchWithTimeout(`${AUTH_BASE_URL}/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
            });
            return r.ok;
        } catch {
            return false;
        } finally {
            queueMicrotask(() => {
                refreshPromise = null;
            });
        }
    })();
    return refreshPromise;
}

/**
 * ¿Vale la pena refrescar ante este response? Sí en 401, y también en 403 cuando viene del
 * verificador de token / authorizer (body sin `code` Fiado, p.ej. `{message:"Forbidden"}`).
 * Un 403 con code (PERMISSION_DENIED) es una denegación real de permiso → NO se refresca.
 */
async function shouldAttemptRefresh(response: Response): Promise<boolean> {
    if (response.status === 401) return true;
    if (response.status !== 403) return false;
    try {
        const body = (await response.clone().json()) as { code?: string };
        return !body.code;
    } catch {
        return true;
    }
}

export async function apiFetch<T = unknown>(
    path: string,
    options: ApiFetchOptions = {},
): Promise<T> {
    const baseUrl = baseUrlForPath(path);
    const { skipRefresh, ...init } = options;
    const buildRequest = (): Promise<Response> => {
        const headers = new Headers({ 'Content-Type': 'application/json' });
        if (init.headers) {
            new Headers(init.headers).forEach((value, key) => {
                headers.set(key, value);
            });
        }
        return fetchWithTimeout(`${baseUrl}${path}`, {
            ...init,
            credentials: 'include',
            headers,
        });
    };

    let response = await buildRequest();

    if (
        !skipRefresh &&
        !path.startsWith('/auth/refresh') &&
        (await shouldAttemptRefresh(response))
    ) {
        const refreshed = await tryRefresh();
        if (refreshed) {
            // Refresh OK → reintenta UNA vez. En el caso stale (Task 13: revocación de
            // permisos → authorizer Deny → 403 sin code Fiado) el inyector recalcula
            // bitset+epoch frescos, así que el retry ya pasa el authorizer.
            response = await buildRequest();
        } else {
            // Refresh falló (refresh token vencido/ausente). La sesión está muerta y NO se
            // reintenta (sin loop). Propagamos SessionExpiredError aunque el response original
            // haya sido un 403 sin code (stale del authorizer) o un 401: así el manejo central
            // de re-login (App.tsx, DEC-008: QueryCache/mutations onError → clear() + /login)
            // se dispara para CUALQUIER origen, no solo para el 401+SESSION_EXPIRED del backend.
            throw new SessionExpiredError({
                code: 'SESSION_EXPIRED',
                date: new Date().toISOString(),
                msg: 'Tu sesión expiró. Inicia sesión de nuevo.',
            });
        }
    }

    if (!response.ok) {
        const err = await parseErrorResponse(response);
        if (response.status >= 500) Sentry.captureException(err);
        throw err;
    }

    const contentType = response.headers.get('Content-Type') ?? '';
    if (response.status === 204 || !contentType.includes('application/json')) {
        return null as T;
    }

    const wrapper = (await response.json()) as ApiSuccessBody<T>;
    return wrapper.data;
}
